import styled, { css } from 'styled-components';

export const BankGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

export const BankCard = styled.button<{ $active?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
  border: none;
  cursor: pointer;
  border-radius: 12px;
  transition: all 0.15s ease;

  ${({ theme, $active }) => css`
    padding: ${theme.spacing?.level4 || '16px'};
    background-color: ${$active
      ? theme.colors?.primaryContainer || '#413ED0'
      : theme.colors?.surfaceContainer || '#1E1E32'};
    border: 1px solid ${$active
      ? theme.colors?.primary || '#C1C1FF'
      : theme.colors?.outlineVariant || '#333'};
    color: ${theme.colors?.onSurface || '#fff'};
    font-family: ${theme.font?.fontFamily || "'Inter Variable', 'Inter', sans-serif"};

    &:hover {
      background-color: ${$active
        ? theme.colors?.primaryContainer || '#413ED0'
        : theme.colors?.surfaceContainerHighest || '#2A2A3E'};
    }
  `}
`;

export const BankName = styled.span`
  font-size: 14px;
  font-weight: 600;
  margin: 0;

  ${({ theme }) => css`
    color: ${theme.colors?.onSurface || '#fff'};
  `}
`;

export const BankId = styled.span`
  font-size: 11px;

  ${({ theme }) => css`
    color: ${theme.colors?.onSurfaceVariant || '#aaa'};
  `}
`;

export const BankMission = styled.span`
  font-size: 12px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;

  ${({ theme }) => css`
    color: ${theme.colors?.onSurfaceVariant || '#aaa'};
  `}
`;

export const StatRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 4px;
`;

export const StatItem = styled.span`
  font-size: 11px;
  font-weight: 600;

  ${({ theme }) => css`
    color: ${theme.colors?.primary || '#C1C1FF'};
  `}
`;

export const MemoryContent = styled.td`
  max-width: 500px;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  line-height: 1.5;
`;

export const TypeFilter = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

export const FilterChip = styled.button<{ $active?: boolean }>`
  border: none;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  padding: 5px 12px;
  border-radius: 100px;
  transition: all 0.15s ease;

  ${({ theme, $active }) => css`
    background-color: ${$active
      ? theme.colors?.primary || '#C1C1FF'
      : theme.colors?.surfaceContainerHigh || '#252540'};
    color: ${$active
      ? theme.colors?.onPrimary || '#1A1A2E'
      : theme.colors?.onSurfaceVariant || '#aaa'};
    font-family: ${theme.font?.fontFamily || "'Inter Variable', 'Inter', sans-serif"};

    &:hover {
      opacity: 0.85;
    }
  `}
`;

export const DispositionBar = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 4px;
`;

export const DispositionItem = styled.span`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${({ theme }) => css`
    color: ${theme.colors?.onSurfaceVariant || '#888'};
  `}
`;

export const DispositionValue = styled.span`
  font-weight: 700;

  ${({ theme }) => css`
    color: ${theme.colors?.onSurface || '#fff'};
  `}
`;

export const BankDetailHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

export const BackButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;

  ${({ theme }) => css`
    color: ${theme.colors?.onSurfaceVariant || '#aaa'};
    font-family: ${theme.font?.fontFamily || "'Inter Variable', 'Inter', sans-serif"};

    &:hover {
      background-color: ${theme.colors?.surfaceContainerHigh || '#252540'};
      color: ${theme.colors?.onSurface || '#fff'};
    }
  `}
`;

export const HeaderControls = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

export const MissionBlock = styled.div`
  margin-top: 16px;
`;

export const MissionText = styled.span`
  font-size: 12px;
  line-height: 1.5;

  ${({ theme }) => css`
    color: ${theme.colors?.onSurface || '#fff'};
  `}
`;

export const DeleteButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  transition: all 0.15s ease;

  ${({ theme }) => css`
    color: ${theme.colors?.onSurfaceVariant || '#888'};

    &:hover {
      color: ${theme.colors?.error || '#F44'};
      background-color: ${theme.colors?.errorContainer || 'rgba(255,68,68,0.1)'};
    }
  `}
`;

export const InlineConfirm = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
`;

export const InlineConfirmText = styled.span`
  font-size: 11px;

  ${({ theme }) => css`
    color: ${theme.colors?.error || '#F44'};
  `}
`;

export const InlineConfirmButton = styled.button<{ $danger?: boolean }>`
  border: none;
  cursor: pointer;
  font-size: 11px;
  font-weight: 500;
  padding: 3px 8px;
  border-radius: 4px;
  transition: all 0.15s ease;

  ${({ theme, $danger }) => css`
    background-color: ${$danger
      ? theme.colors?.error || '#F44'
      : theme.colors?.surfaceContainerHigh || '#252540'};
    color: ${$danger
      ? theme.colors?.onError || '#fff'
      : theme.colors?.onSurfaceVariant || '#aaa'};
    font-family: ${theme.font?.fontFamily || "'Inter Variable', 'Inter', sans-serif"};

    &:hover {
      opacity: 0.85;
    }
  `}
`;

export const GraphButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 6px;
  transition: all 0.15s ease;
  margin-left: auto;

  ${({ theme }) => css`
    background-color: ${theme.colors?.surfaceContainerHigh || '#252540'};
    color: ${theme.colors?.onSurface || '#fff'};
    font-family: ${theme.font?.fontFamily || "'Inter Variable', 'Inter', sans-serif"};

    &:hover {
      background-color: ${theme.colors?.primaryContainer || '#413ED0'};
      color: ${theme.colors?.onPrimaryContainer || '#fff'};
    }
  `}
`;

export const ReflectButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 6px;
  transition: all 0.15s ease;

  ${({ theme }) => css`
    background-color: ${theme.colors?.surfaceContainerHigh || '#252540'};
    color: ${theme.colors?.onSurface || '#fff'};
    font-family: ${theme.font?.fontFamily || "'Inter Variable', 'Inter', sans-serif"};

    &:hover {
      background-color: ${theme.colors?.primaryContainer || '#413ED0'};
      color: ${theme.colors?.onPrimaryContainer || '#fff'};
    }
  `}
`;

export const DangerButton = styled.button`
  border: none;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 6px;
  transition: all 0.15s ease;

  ${({ theme }) => css`
    background-color: ${theme.colors?.errorContainer || 'rgba(255,68,68,0.15)'};
    color: ${theme.colors?.error || '#F44'};
    font-family: ${theme.font?.fontFamily || "'Inter Variable', 'Inter', sans-serif"};

    &:hover {
      background-color: ${theme.colors?.error || '#F44'};
      color: ${theme.colors?.onError || '#fff'};
    }
  `}
`;

export const ConfirmOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.5);
`;

export const ConfirmDialog = styled.div`
  max-width: 400px;
  width: 90%;
  border-radius: 12px;
  padding: 24px;

  ${({ theme }) => css`
    background-color: ${theme.colors?.surfaceContainer || '#1E1E32'};
    border: 1px solid ${theme.colors?.outlineVariant || '#333'};
    color: ${theme.colors?.onSurface || '#fff'};
    font-family: ${theme.font?.fontFamily || "'Inter Variable', 'Inter', sans-serif"};
  `}
`;

export const ConfirmTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 600;
`;

export const ConfirmBody = styled.p`
  margin: 0 0 20px;
  font-size: 13px;
  line-height: 1.5;

  ${({ theme }) => css`
    color: ${theme.colors?.onSurfaceVariant || '#aaa'};
  `}
`;

export const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;
